import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  SUPPORT_TOPIC_LABEL,
  supportTopicSchema,
  type HelpArticle,
  type SupportTopicValue,
} from '@teentack/shared';
import { useAuth } from '@/auth/use-auth';
import { useCreateTicket, useHelp, useMyTickets } from '@/features/support/use-support';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import { Input, SelectInput, Textarea } from '@/components/ui/Input';
import { ErrorNote, InfoNote } from '@/components/Form';
import { BookIcon, ShieldCheckIcon } from '@/components/ui/Icons';
import { relativeTime } from '@/lib/relative-time';
import { presentApiError } from '@/lib/form-errors';

/**
 * Community and support. [Parent step 28]
 *
 * Answers first, form second. Most people arrive with a question somebody has
 * already asked, and making them fill in a ticket to find that out is how a
 * help centre becomes a queue. The emergency route is called out at the top,
 * because "I feel unsafe" must never be filed as a support request.
 */

function Article({ article }: { article: HelpArticle }) {
  return (
    <details className="group border-b border-line last:border-b-0">
      <summary className="cursor-pointer list-none px-5 py-4 text-sm font-semibold text-ink transition hover:bg-brand-50/50">
        <span className="inline-flex w-full items-center justify-between gap-3">
          {article.question}
          <span
            aria-hidden="true"
            className="text-slate-400 transition group-open:rotate-45"
          >
            +
          </span>
        </span>
      </summary>
      <p className="px-5 pb-4 text-sm leading-relaxed text-slate-600">{article.answer}</p>
    </details>
  );
}

function TicketForm() {
  const [topic, setTopic] = useState<SupportTopicValue>('ACCOUNT');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const create = useCreateTicket();

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({ topic, subject: subject.trim(), message: message.trim() });
      setSubject('');
      setMessage('');
      setSent(true);
    } catch (caught) {
      setError(presentApiError(caught, 'Could not send that').message);
    }
  }

  if (sent) {
    return (
      <div className="space-y-3">
        <InfoNote>
          Sent. We will come back to you in your notifications, and it is listed below.
        </InfoNote>
        <Button variant="outline" onClick={() => setSent(false)}>
          Ask something else
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={(event) => void submit(event)} className="space-y-3">
      <SelectInput
        label="What is it about?"
        value={topic}
        onChange={(event) => setTopic(event.target.value as SupportTopicValue)}
      >
        {supportTopicSchema.options.map((value) => (
          <option key={value} value={value}>
            {SUPPORT_TOPIC_LABEL[value]}
          </option>
        ))}
      </SelectInput>

      <Input
        label="Subject"
        value={subject}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSubject(event.target.value)}
        maxLength={150}
        placeholder="A few words"
      />

      <Textarea
        label="What is going on?"
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        rows={4}
        maxLength={4000}
        placeholder="As much or as little as you like."
      />

      {error && <ErrorNote message={error} />}

      <Button
        type="submit"
        disabled={create.isPending || subject.trim().length < 3 || message.trim().length < 10}
      >
        {create.isPending ? 'Sending…' : 'Send'}
      </Button>
    </form>
  );
}

export function SupportPage() {
  const { user } = useAuth();
  const help = useHelp();
  const tickets = useMyTickets({ pageSize: 20 }, Boolean(user));


  /** Grouped so the page reads as topics rather than one long list. */
  const topics = useMemo(() => {
    const grouped = new Map<string, HelpArticle[]>();
    for (const article of help.data?.articles ?? []) {
      const existing = grouped.get(article.topic);
      if (existing) existing.push(article);
      else grouped.set(article.topic, [article]);
    }
    return [...grouped.entries()];
  }, [help.data]);

  return (
    <main className="container-page py-10">
      <div className="mx-auto max-w-3xl">
        <header className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
            Help and support
          </p>
          <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
            How can we help?
          </h1>
          <p className="mt-2 max-w-prose text-sm leading-relaxed text-slate-500">
            Answers to the things people ask most, and a way to reach us when they are not here.
          </p>
        </header>

        {/* An emergency must never be filed as a support ticket. */}
        <div className="mb-8 flex flex-wrap items-center gap-3 rounded-2xl border border-sun-300 bg-sun-50 px-5 py-4">
          <ShieldCheckIcon className="size-5 shrink-0 text-sun-700" />
          <p className="flex-1 text-sm font-medium text-sun-900">
            If something feels unsafe right now, do not use this form.
          </p>
          <Link
            to="/safety"
            className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
          >
            Get help now
          </Link>
        </div>

        {help.isPending && (
          <div className="space-y-3" aria-busy="true">
            <Skeleton className="h-14" />
            <Skeleton className="h-14" />
          </div>
        )}

        {topics.map(([topic, entries]) => (
          <section key={topic} className="mb-6">
            <h2 className="mb-2 inline-flex items-center gap-2 text-sm font-bold text-ink">
              <BookIcon className="size-4 text-slate-400" />
              {topic}
            </h2>
            <div className="overflow-hidden rounded-2xl border border-line bg-white shadow-soft">
              {entries.map((article) => (
                <Article key={article.slug} article={article} />
              ))}
            </div>
          </section>
        ))}

        {user && (
          <>
            <section className="mt-10">
              <h2 className="text-base font-bold text-ink">Still stuck?</h2>
              <p className="mb-4 mt-1 text-sm text-slate-500">
                Tell us what is going on and we will reply in your notifications.
              </p>
              <div className="rounded-2xl border border-line bg-white p-5 shadow-soft">
                <TicketForm />
              </div>
            </section>

            {tickets.data && tickets.data.items.length > 0 && (
              <section className="mt-10">
                <h2 className="mb-4 text-base font-bold text-ink">What you have asked</h2>
                <ul className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-white shadow-soft">
                  {tickets.data.items.map((ticket) => (
                    <li key={ticket.id} className="px-5 py-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="text-sm font-bold text-ink">{ticket.subject}</p>
                        <Badge tone={ticket.status === 'OPEN' ? 'brand' : 'safe'}>
                          {ticket.status.charAt(0) + ticket.status.slice(1).toLowerCase()}
                        </Badge>
                      </div>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">
                        {ticket.message}
                      </p>
                      <p className="mt-1 text-xs text-slate-400">
                        {SUPPORT_TOPIC_LABEL[ticket.topic]} · {relativeTime(ticket.createdAt)}
                      </p>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}

        {help.data && (
          <p className="mt-10 text-center text-sm text-slate-500">
            Prefer email? Write to{' '}
            <a
              href={`mailto:${help.data.contactEmail}`}
              className="font-semibold text-brand-600 hover:underline"
            >
              {help.data.contactEmail}
            </a>
            .
          </p>
        )}
      </div>
    </main>
  );
}
